import React, { useState, useEffect, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  FaLightbulb, 
  FaUser, 
  FaSignOutAlt, 
  FaCaretDown, 
  FaSyncAlt, 
  FaPlusCircle,
  FaArrowLeft,
  FaRobot,
  FaPencilAlt,
  FaCheck,
  FaTimes
} from 'react-icons/fa';
import { supabase } from '../lib/supabase';
import { useModel } from '../contexts/ModelContext';
import './Header.css';

interface HeaderProps {
  selectedIdeas?: any[];
  selectedIdeasContent?: any[];
  generateButton?: ReactNode;
  projectName?: string;
  onEditProjectName?: (newName: string) => void;
  isEditingProjectName?: boolean;
  onStartEditing?: () => void;
  editedProjectName?: string;
  onChangeProjectName?: (name: string) => void;
  onCancelEditing?: () => void;
  children?: ReactNode;
}

const Header: React.FC<HeaderProps> = ({ 
  selectedIdeas = [], 
  selectedIdeasContent = [], 
  generateButton,
  projectName,
  onEditProjectName,
  isEditingProjectName,
  onStartEditing,
  editedProjectName,
  onChangeProjectName,
  onCancelEditing,
  children 
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const { selectedModel, setSelectedModel, availableModels } = useModel();
  const navigate = useNavigate();
  const location = useLocation();
  const topic = location.state?.topic || '';

  useEffect(() => {
    const user = supabase.auth.getUser();
    user.then(({ data }) => {
      if (data.user) {
        setUserEmail(data.user.email || '');
      }
    });
  }, []);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      window.location.reload();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleModelChange = (model: string) => {
    setSelectedModel(model as keyof typeof availableModels);
    setShowDropdown(false);
  };

  return (
    <>
      <div className="header">
        <div className="header-content">
          <div className="header-left" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <FaLightbulb className="header-icon" />
            <h1>Idea Builder</h1>
          </div>
          
          {projectName && (
            <div className="header-center">
              <div className="project-title-container">
                {isEditingProjectName ? (
                  <div className="project-title-edit">
                    <input
                      type="text"
                      value={editedProjectName}
                      onChange={(e) => onChangeProjectName?.(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          onEditProjectName?.(editedProjectName || '');
                        } else if (e.key === 'Escape') {
                          onCancelEditing?.();
                        }
                      }}
                      placeholder="Enter project name"
                      autoFocus
                    />
                    <div className="edit-actions">
                      <button 
                        className="icon-button"
                        onClick={() => onEditProjectName?.(editedProjectName || '')}
                      >
                        <FaCheck />
                      </button>
                      <button 
                        className="icon-button"
                        onClick={onCancelEditing}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <h2 className="project-title">{projectName}</h2>
                    {onStartEditing && (
                      <button
                        className="icon-button edit-project-name"
                        onClick={onStartEditing}
                      >
                        <FaPencilAlt />
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {location.pathname === '/ideation' && topic && (
            <div className="header-center">
              <h2 className="topic-title">{topic}</h2>
            </div>
          )}

          <div className="header-right">
            {generateButton}
            <div className="user-menu" onClick={() => setShowDropdown(!showDropdown)}>
              <FaUser className="user-icon" />
              <span className="user-email">{userEmail}</span>
              <FaCaretDown className="dropdown-icon" />
              
              {showDropdown && (
                <div className="dropdown-menu">
                  <div className="model-selection">
                    <div className="dropdown-section-title">
                      <FaRobot />
                      <span>AI Model</span>
                    </div>
                    {Object.entries(availableModels).map(([modelName, config]) => (
                      <button
                        key={modelName}
                        onClick={() => handleModelChange(modelName)}
                        className={`dropdown-item ${modelName === selectedModel ? 'active' : ''}`}
                      >
                        <span>{config.name}</span>
                        {modelName === selectedModel && <span className="checkmark">✓</span>}
                      </button>
                    ))}
                  </div>
                  <div className="dropdown-divider" />
                  <button onClick={handleSignOut} className="dropdown-item">
                    <FaSignOutAlt />
                    <span>Sign Out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {location.pathname === '/ideation' && (
        <div className="sidebar">
          <div className="sidebar-buttons">
            <button 
              onClick={handleBack}
              className="sidebar-button"
              data-tooltip-id="main-tooltip"
              data-tooltip-content="Go back"
            >
              <FaArrowLeft />
              <span>Back</span>
            </button>
            <button 
              onClick={() => navigate('/synthesis', { 
                state: { selectedIdeasContent } 
              })}
              className="sidebar-button"
              data-tooltip-id="main-tooltip"
              data-tooltip-content={selectedIdeas.length === 0 ? "Select ideas to synthesize" : "Synthesize selected ideas"}
              disabled={selectedIdeas.length === 0}
            >
              <FaSyncAlt />
              <span>Synthesize</span>
            </button>
            <button 
              onClick={() => navigate('/')}
              className="sidebar-button"
              data-tooltip-id="main-tooltip"
              data-tooltip-content="Start a new topic"
            >
              <FaPlusCircle />
              <span>New Topic</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Header; 